import { GRAPHQL_URL, BASE_URL } from '../constants';

/**
 * The service to handle storing authentication properties.
 *
 * Security compromise for headless frontend auth:
 * - Access token is stored in memory only.
 * - Refresh token is stored in sessionStorage.
 * - No tokens are stored in localStorage.
 */
export default class AuthenticationService {
	static authToken = '';

	get refreshTokenKey() {
		return this.getRefreshTokenKey();
	}

	get tokenKey() {
		return this.getTokenKey();
	}

	get jwtTokenFromStorage() {
		return this.getJwtTokenFromStorage();
	}

	get jwtRefreshTokenFromStorage() {
		return this.getJwtRefreshTokenFromStorage();
	}

	getRefreshTokenKey() {
		return 'tz9k9avyicthfb09iojttz9k9avyicthfb09iojt';
	}

	getTokenKey() {
		return '9shrihkg6j0qcz33pcxx9shrihkg6j0qcz33pcxx';
	}

	/**
	 * Access token is memory-only.
	 */
	getJwtTokenFromStorage() {
		return AuthenticationService.authToken || '';
	}

	/**
	 * Refresh token is stored for the tab/session only.
	 */
	getJwtRefreshTokenFromStorage() {
		const token = sessionStorage.getItem(this.getRefreshTokenKey());
		return token || '';
	}

	/**
	 * Store access token in memory only.
	 */
	setTokenInStorage(token) {
		AuthenticationService.authToken = token || '';
	}

	/**
	 * Store refresh token in sessionStorage.
	 */
	setJwtRefreshToken(token) {
		if (!token) {
			sessionStorage.removeItem(this.getRefreshTokenKey());
			return;
		}

		sessionStorage.setItem(this.getRefreshTokenKey(), token);
	}

	deleteToken(key) {
		if (key === this.getTokenKey()) {
			AuthenticationService.authToken = '';
			return;
		}

		sessionStorage.removeItem(key);
	}

	logUserOut() {
		AuthenticationService.authToken = '';
		sessionStorage.removeItem(this.getRefreshTokenKey());
		sessionStorage.removeItem(this.getTokenKey());
	}

	refreshToken =
		(initialRequest = false) =>
		async (dispatch) => {
			const refreshToken = this.jwtRefreshTokenFromStorage;

			if (!refreshToken) {
				this.logUserOut();

				dispatch({
					type: 'USER_LOGGED_IN',
					payload: false,
					authStatus: 'finished',
				});

				return false;
			}

			try {
				const response = await fetch(GRAPHQL_URL, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						query: `
						mutation RefreshAuthToken($jwtRefreshToken: String!) {
							refreshJwtAuthToken(
								input: {
									clientMutationId: "refreshToken"
									jwtRefreshToken: $jwtRefreshToken
								}
							) {
								authToken
							}
						}
					`,
						variables: {
							jwtRefreshToken: refreshToken,
						},
					}),
				});

				const data = await response.json();

				if (!response.ok || data.errors) {
					this.logUserOut();

					dispatch({
						type: 'USER_LOGGED_IN',
						payload: false,
						authStatus: 'finished',
					});

					return false;
				}

				const newAuthToken = data?.data?.refreshJwtAuthToken?.authToken || false;

				if (!newAuthToken) {
					this.logUserOut();

					dispatch({
						type: 'USER_LOGGED_IN',
						authStatus: 'finished',
						payload: false,
					});

					return false;
				}

				this.setTokenInStorage(newAuthToken);

				const loadProspectus = await fetch(`${BASE_URL}/wp-json/edu_api/get/prospectus`, {
					method: 'POST',
					headers: {
						Authorization: `Bearer ${newAuthToken}`,
						'Content-Type': 'application/json',
					},
				});

				const prospectusData = await loadProspectus.json();

				dispatch({
					type: 'USER_LOGGED_IN',
					authStatus: 'finished',
					payload: true,
				});

				if (prospectusData) {
					
					dispatch({
						type: 'FOUND_PROSPECTUS',
						payload: {
							data: prospectusData.data,
							prospectusId: prospectusData.id,
							selectedWayfinderAnswers: prospectusData.selectedTerms || {},
						},
					});
				}

				return true;
			} catch (error) {
				console.error('Token refresh failed:', error);

				this.logUserOut();

				dispatch({
					type: 'USER_LOGGED_IN',
					payload: false,
					authStatus: 'finished',
				});

				return false;
			}
		};
}
